import { createSlice } from "@reduxjs/toolkit"

import { EggspeditionSpeciesGet, EggspeditionSpeciesPut } from "./eggspedition"

/**
 * De soortrecords uit `GET /eggspedition/species`: per WoRMS-taxon de wetenschappelijke en Engelse
 * naam, en elke schrijfwijze die ernaar wijst. Waarnemingen houden de naam waarmee ze zijn
 * opgeslagen; via deze records is "striped parrotfish" dezelfde soort als "Scarus iseri".
 *
 * Eén keer per sessie opgehaald, net als de duiklocaties; wat iemand daarna kiest komt er via
 * `EggspeditionSpeciesSave` bij.
 */
export const speciesSlice = createSlice({
  name: "species",
  initialState: { items: [], loaded: false, loading: false },
  reducers: {
    loading: (state) => ({ ...state, loading: true }),
    // Samenvoegen, niet vervangen: wie een soort kiest terwijl de lijst nog onderweg is, heeft
    // hem al toegevoegd, en het antwoord van daarvoor kent hem nog niet.
    set: (state, action) => ({
      items: [...action.payload, ...state.items.filter(item => !action.payload.some(record => record.aphia_id === item.aphia_id))],
      loaded: true,
      loading: false
    }),
    upsert: (state, action) => ({
      ...state,
      items: [...state.items.filter(item => item.aphia_id !== action.payload.aphia_id), action.payload]
    })
  }
})

export const { loading, set, upsert } = speciesSlice.actions

const normalise = (name) => String(name ?? "").trim().toLowerCase()

/** Een opzoekfunctie over de records: op scientificNameID, anders op een bekende schrijfwijze. */
export const speciesIndex = (items) => {
  const byId = new Map(items.map(item => [item.scientific_name_id, item]))
  const byName = new Map(items.flatMap(item =>
    [item.scientific_name, item.vernacular_name, ...item.names].filter(Boolean).map(name => [normalise(name), item])))

  return (name, nameId) => byId.get(nameId) || byName.get(normalise(name)) || null
}

/** Hoe een soort heet in de app: voorlopig de Engelse naam, anders de wetenschappelijke. */
export const speciesDisplayName = (record, name) => record?.vernacular_name || record?.scientific_name || name || null

export const EggspeditionSpeciesLoad = (signal, token) => async (dispatch) => {
  dispatch(loading())
  const items = await dispatch(EggspeditionSpeciesGet(signal, token))
  if (items) dispatch(set(items))
}

/** Een gekozen WoRMS-taxon vastleggen als soortrecord. */
export const EggspeditionSpeciesSave = (signal, token, taxon) => async (dispatch) => {
  const record = await dispatch(EggspeditionSpeciesPut(signal, token, { aphia_id: taxon.AphiaID },
    { scientific_name: taxon.scientific_name, vernacular_name: taxon.english_name, rank: taxon.rank, names: [] }))
  if (record) dispatch(upsert(record))
  return record
}

export default speciesSlice.reducer
